// Unified PhotoSwipe Manager
// Handles both product gallery and supplement facts PhotoSwipe functionality
class UnifiedPhotoSwipeManager {
  constructor() {
    if (UnifiedPhotoSwipeManager.instance) {
      return UnifiedPhotoSwipeManager.instance;
    }

    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.PhotoSwipe = null;
    this.currentInstance = null;

    UnifiedPhotoSwipeManager.instance = this;
    window.unifiedPhotoSwipeManager = this;

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeAll());
    } else {
      this.initializeAll();
    }
  }

  async initializeAll() {
    // Initialize Swiper components first
    this.initSwipers();

    // Import PhotoSwipe ESM using Shopify asset URL
    // Fallback to window.PhotoSwipe if already loaded
    try {
      if (!window.PhotoSwipe) {
        const mod = await import('//neocell.co.uk/cdn/shop/t/2/assets/photoswipe.min.js?v=128169069546462557921778701468');
        this.PhotoSwipe = mod.default || mod.PhotoSwipe || mod;
        window.PhotoSwipe = this.PhotoSwipe; // expose for other scripts relying on global
      } else {
        this.PhotoSwipe = window.PhotoSwipe;
      }
    } catch (e) {
      console.warn('[UnifiedPhotoSwipeManager] Failed to import PhotoSwipe ESM, attempting global fallback.', e);
      this.PhotoSwipe = window.PhotoSwipe;
    }

    // Set up all PhotoSwipe functionality
    this.setupGalleryLightbox();
    this.setupSupplementFacts();
  }

  // === SWIPER FUNCTIONALITY ===
  initSwipers() {
    this.initThumbnailSwiper();
    this.initMainSwiper();
  }

  initThumbnailSwiper() {
    const thumbsElement = document.querySelector('.product-gallery-thumbs');
    if (!thumbsElement) return;

    // Count the number of images to determine how many to show
    const slideCount = thumbsElement.querySelectorAll('.swiper-slide').length;

    // If there are too many images (more than 6), limit the display and allow some scrolling
    const maxVisible = 6;
    const showAll = slideCount <= maxVisible;

    this.thumbsSwiper = new Swiper('.product-gallery-thumbs', {
      spaceBetween: 8,
      slidesPerView: showAll ? slideCount : 'auto', // Show all if <= 6, otherwise auto
      freeMode: !showAll, // Enable free mode only if we have many images
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      allowTouchMove: !showAll, // Disable touch/drag if showing all
      centeredSlides: !showAll, // Center slides if scrolling is enabled
      breakpoints: {
        768: {
          slidesPerView: showAll ? slideCount : 'auto',
          spaceBetween: 12,
          allowTouchMove: !showAll,
        },
        1024: {
          slidesPerView: showAll ? slideCount : 'auto',
          spaceBetween: 16,
          allowTouchMove: !showAll,
        }
      }
    });

    // Store whether we're showing all thumbs for the update method
    this.showingAllThumbs = showAll;
  }

  initMainSwiper() {
    const mainElement = document.querySelector('.product-gallery-main');
    if (!mainElement) return;

    this.mainSwiper = new Swiper('.product-gallery-main', {
      spaceBetween: 0,
      loop: false,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: true,
      },
      thumbs: {
        swiper: this.thumbsSwiper,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      mousewheel: {
        invert: false,
      },
      on: {
        slideChange: () => {
          this.updateThumbnails();
        }
      }
    });
  }

  updateThumbnails() {
    if (!this.thumbsSwiper || !this.mainSwiper) return;

    const activeIndex = this.mainSwiper.realIndex;

    if (this.showingAllThumbs) {
      // Static thumbnail display - just update active classes
      this.thumbsSwiper.slides.forEach(slide => {
        slide.classList.remove('swiper-slide-thumb-active');
      });

      if (this.thumbsSwiper.slides[activeIndex]) {
        this.thumbsSwiper.slides[activeIndex].classList.add('swiper-slide-thumb-active');
      }
    } else {
      // Many thumbnails - slide to center the active one
      this.thumbsSwiper.slideTo(activeIndex);
    }
  }

  // (Removed waitForPhotoSwipe): direct ESM import strategy used instead.

  // === GALLERY LIGHTBOX ===
  setupGalleryLightbox() {
    const containers = document.querySelectorAll('.gallery-image-container');
    if (!containers.length) return;

    // Build photo items array
    this.galleryItems = Array.from(containers).map(container => ({
      src: container.dataset.pswpSrc || container.querySelector('img').src,
      width: parseInt(container.dataset.pswpWidth) || 1200,
      height: parseInt(container.dataset.pswpHeight) || 1200,
      alt: container.querySelector('img')?.alt || 'Product Image'
    }));

    // Add click handlers
    containers.forEach((container, index) => {
      container.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openGalleryLightbox(index);
      });
    });
  }

  openGalleryLightbox(index = 0) {
    if (!this.PhotoSwipe || !this.galleryItems) return;

    this.closeCurrentInstance();

    const lightbox = new this.PhotoSwipe({
      dataSource: this.galleryItems,
      index: index,
      bgOpacity: 0.9,
      showHideAnimationType: 'fade',
      wheelToZoom: true,
      pinchToClose: false,
      closeOnVerticalDrag: true,
      padding: { top: 20, bottom: 40, left: 100, right: 100 }
    });

    this.currentInstance = lightbox;
    window.photoSwipeCurrentInstance = lightbox;

    // Sync with swiper
    lightbox.on('change', () => {
      if (this.mainSwiper) {
        this.mainSwiper.slideTo(lightbox.currIndex);
      }
    });

    lightbox.on('close', () => {
      this.currentInstance = null;
      window.photoSwipeCurrentInstance = null;
    });

    lightbox.init();
  }

  // === SUPPLEMENT FACTS ===
  setupSupplementFacts() {
    const trigger = document.querySelector('.supplement-facts-trigger');
    const container = document.querySelector('.supplement-facts-container');

    if (!trigger || !container) return;

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openSupplementFacts();
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        this.openSupplementFacts();
      }
    });
  }

  async openSupplementFacts() {
    if (!this.PhotoSwipe) return;

    const container = document.querySelector('.supplement-facts-container');
    if (!container) return;

    const src = container.dataset.pswpSrc;
    const width = parseInt(container.dataset.pswpWidth) || 1200;
    const height = parseInt(container.dataset.pswpHeight) || 1200;

    if (!src) return;

    this.closeCurrentInstance();

    const item = {
      src: src,
      width: width,
      height: height,
      alt: 'Supplement Facts'
    };

    const lightbox = new this.PhotoSwipe({
      dataSource: [item],
      index: 0,
      bgOpacity: 0.9,
      showHideAnimationType: 'zoom',
      allowPanToNext: false,
      loop: false,
      preload: [1, 1]
    });

    this.currentInstance = lightbox;
    window.photoSwipeCurrentInstance = lightbox;

    lightbox.on('close', () => {
      this.currentInstance = null;
      window.photoSwipeCurrentInstance = null;
    });

    lightbox.init();
  }

  // === UTILITY METHODS ===
  closeCurrentInstance() {
    if (this.currentInstance) {
      this.currentInstance.close();
      this.currentInstance = null;
      window.photoSwipeCurrentInstance = null;
    }
  }

  // Public API for external access
  openLightbox(index = 0) {
    this.openGalleryLightbox(index);
  }
}

// Initialize when Swiper is available
function initUnifiedPhotoSwipeManager() {
  if (window.unifiedPhotoSwipeManager) return;

  if (typeof Swiper !== 'undefined') {
    new UnifiedPhotoSwipeManager();
  } else {
    setTimeout(initUnifiedPhotoSwipeManager, 100);
  }
}

initUnifiedPhotoSwipeManager();